/**
 * Every tunable number in one place. The balance sim (`npm run sim`) and
 * tests/unit/balance.test.ts check these against the pacing targets in
 * docs/plans/v1.0-rewrite.md §5. Change numbers here, not in core/.
 */

export const TICK_MS = 100;

// ── Farm structure ────────────────────────────────────────────────────
export const BOXES_PER_FLOCK = 15;
export const MAX_FLOCKS = 10;
export const STARTER_BOXES = 2;

/** Nesting box price ladder (index = boxes bought so far across all flocks, after the 2 free ones). */
export const NEST_BOX_COSTS = [65, 100, 150, 250, 400, 650, 1000, 1600, 2500, 4000, 6500, 10_000, 16_000] as const;
/** Every flock after the first costs more: hens, boxes and coop upgrades × FLOCK_TAX^(flockIndex). */
export const FLOCK_TAX = 1.5;

/** Nest capacity: eggs a nest can hold before laid eggs are lost. */
export const NEST_BASE_CAPACITY = 4;
export const NEST_MAX_CAPACITY = 24;
export const NEST_UPGRADE_COSTS = [
  100, 150, 250, 400, 650, 1000, 1500, 2500, 4000, 6500,
  10_000, 15_000, 20_000, 30_000, 40_000, 50_000, 75_000, 100_000, 150_000, 200_000,
] as const;

/** Hard ceiling on one hen's laying rate (eggs/s). */
export const MAX_LAY_RATE = 2;
/** Ceiling on the product of stacked transient buffs, per stream. */
export const BUFF_CAP = 25;

// ── Roosters ──────────────────────────────────────────────────────────
/** Total hens needed to open rooster slot N (index 0 = first rooster). */
export const ROOSTER_SLOT_HENS = [5, 15, 30, 45, 60, 75, 90, 105, 120, 135] as const;
/**
 * Price of the Nth rooster (index 0 = first). Act 2 pacing lives here.
 * Calibrated with `npm run sim -- --act2` so an engaged idle routine (10-min
 * sessions every ~4h) buys R2 ≈ 8h, R5 ≈ 36h and R10 ≈ 124h after the win.
 */
export const ROOSTER_COSTS = [
  500, 450_000_000, 1_400_000_000, 4_000_000_000, 10_000_000_000,
  22_000_000_000, 45_000_000_000, 80_000_000_000, 125_000_000_000, 200_000_000_000,
] as const;
/** Rooster-egg cockerel voucher: discount per voucher, and the most that can stack. */
export const VOUCHER_DISCOUNT = 0.25;
export const VOUCHER_MAX = 2;

// ── Collection ────────────────────────────────────────────────────────
/** Eggs taken per click at click-gather level 0 (before upgrades). */
export const BASE_CLICK_GATHER = 1;

// ── Combo ─────────────────────────────────────────────────────────────
export const COMBO_MIN_CPS = 3;
export const COMBO_HOLD_MS = 5000;
export const COMBO_DECAY_MS = 1000;
export const COMBO_MAX_TIER = 10;
export const COMBO_CLICK_STEP = 0.1;   // +0.1× click value per tier
export const COMBO_VALUE_STEP = 0.005; // +0.5% global egg value per tier
export const ACCELERATOR_STEP = 0.1;   // banked offline ms per online ms, per tier

// ── Offline / Act 2 ───────────────────────────────────────────────────
export const OFFLINE_EFFICIENCY = 0.8;
export const OFFLINE_BASE_CAP_HOURS = 2;
export const OFFLINE_CAP_HOURS_PER_ROOSTER = 2;
export const OFFLINE_MIN_MS = 5_000;
export const WELCOME_BACK_MIN_MS = 5 * 60_000;

// ── Win ───────────────────────────────────────────────────────────────
export const WIN_BALANCE = 1_000_000;
export const WIN_HENS = 15;

// ── Events (mean seconds between spawns while eligible; max 1 of each unless noted) ──
export const EVENTS = {
  firstEventDelaySec: 10,
  cadenceFloorSec: 90,
  stuckWindowSec: 600,
  yolkBurst: { meanSec: 90, lifeSec: 15, max: 2, minLifetime: 0 },
  eggShower: { meanSec: 150, lifeSec: 12, minLifetime: 500, eggsMin: 8, eggsMax: 12 },
  doubleTime: { meanSec: 180, lifeSec: 12, minLifetime: 5_000, layMult: 2, durationSec: 30 },
  silver: { meanSec: 300, lifeSec: 12, minLifetime: 5_000, payoutSec: 15, stormPayoutSec: 45, stormGatherMult: 3, stormDurationSec: 20 },
  golden: { meanSec: 180, lifeSec: 15, minLifetime: 100_000 },
  rotten: { meanSec: 360, salvageSec: 30, debuffValue: 0.85, debuffSec: 30, salvagePayoutSec: 15, salvageFloor: 15 },
  rainbow: { meanSec: 600, lifeSec: 20 },
  roosterEgg: { meanSec: 1500, lifeSec: 45, incubateMinSec: 300, incubateMaxSec: 600 },
  bloom: { intervalSec: 120, pairedIntervalSec: 60, lifeSec: 15 },
} as const;

/** Golden egg effects (weights sum to 1). */
export const GOLDEN_EFFECTS = {
  burst: { weight: 0.25, payoutSec: 30 },
  layingFrenzy: { weight: 0.30, layMult: 3, durationSec: 30 },
  luckyClutch: { weight: 0.25, mult: 3, eggs: 10 },
  clickStorm: { weight: 0.20, clickMult: 5, layMult: 2, durationSec: 15 },
} as const;

export const RAINBOW_EFFECTS = {
  harmony: { layMult: 3, autoMult: 3, durationSec: 20 },
  prismatic: { mult: 5, eggs: 20 },
  rush: { gatherMult: 3, clickMult: 5, durationSec: 15 },
} as const;

// ── Critters (ambient clickables; reward = max(floor, production$/s × seconds)) ──
export const CRITTERS = {
  maxOnScreen: 5,
  chick: { everyMinSec: 20, everyMaxSec: 40, lifeSec: 12, floor: 3, minSec: 1, maxSec: 3 },
  feather: { everyMinSec: 30, everyMaxSec: 60, lifeSec: 10, floor: 2, minSec: 0.5, maxSec: 1.5 },
  worm: { everyMinSec: 15, everyMaxSec: 30, lifeSec: 10, floor: 5, minSec: 2, maxSec: 4, maxLifetime: 100_000, megaChance: 0.05 },
  megaWorm: { lifeSec: 14, floor: 50, minSec: 15, maxSec: 30, hp: 3 },
  butterfly: { everyMinSec: 45, everyMaxSec: 90, lifeSec: 10, layMult: 1.5, durationSec: 10, maxLifetime: 100_000 },
  bird: { everyMinSec: 120, everyMaxSec: 240, lifeSec: 15, floor: 15, minSec: 8, maxSec: 15, minLifetime: 10_000 },
  /** Storm eggs (Egg Shower / Egg Storm): small, plentiful, not multiplied by the storm's click bonus. */
  stormEgg: { lifeSec: 4, floor: 5, minSec: 1, maxSec: 3, waveEverySec: 0.8, waveMin: 2, waveMax: 3 },
} as const;

// ── Broody ────────────────────────────────────────────────────────────
/** Laying phase before a broody-prone hen goes broody, by broodiness. */
export const LAYING_PHASE_SEC: Record<number, number> = { 5: 120, 4: 180, 3: 240, 2: 300, 1: 360 };
export const BROOD_DURATION_SEC = 480;
export const BROODY_AUTO_ACCEPT_SEC = 60;
export const BROODY_PRONE_VALUE_BONUS = 1.3;
export const BROOD_FAIL_CONSOLATION = 20;
export const BROOD_REWARD_FLOOR = 50;
/** A successful brood pays this multiple of what the hen would have laid during the brood. */
export const BROOD_REWARD_MULT = 1.25;
export const MAX_BROOD_EGGS_ON_SCREEN = 3;

// ── Breed bonuses ─────────────────────────────────────────────────────
export const FIRST_OF_KIND_BONUS = 1.75;

// ── Hen Wrangler ──────────────────────────────────────────────────────
/** Barnevelder `achievement_affinity` lowers Hen Wrangler achievement thresholds by this fraction. */
export const BARNEVELDER_THRESHOLD_CUT = 0.2;
